// Where you are, as a value.
//
// NO ROUTER DEPENDENCY, AND THE REASON IS THE SIZE OF THE PROBLEM RATHER THAN A
// POSITION ON DEPENDENCIES. There are a handful of places to be, one of which
// carries a report name. A routing library brings a context, a matcher, a
// history abstraction and a release cadence, in order to solve a problem that is
// one regular expression and a switch. xp/kiosk made the same call about the
// same class of thing.
//
// PATHS RATHER THAN A HASH, WHICH COSTS ONE LINE OF HOSTING CONFIGURATION.
// Every path here has to be rewritten to index.html by whatever serves the
// bundle, and README.md says so under deployment, because the failure mode is
// specific and looks like our bug: a person bookmarks an activation, comes back
// the next morning, and gets the host's 404 page. A hash route would need no
// rewrite and is chosen against because the url is the one part of this console
// people will paste to each other, and an id after a `#` is dropped by anything
// that tries to be helpful with links.
//
// WHAT IS DELIBERATELY NOT IN A URL. The session, in any form. Not as a token,
// not as an id, not as a "remember me" parameter. A url is pasted into chat
// messages, kept in history, sent as a referrer and written to server logs, and
// index.html sets `referrer: no-referrer` for the narrower version of the same
// worry about the activation id. session/store.ts holds the rest of that
// argument.
//
// AN ACTIVATION ID IS IN THE URL AND THAT IS A JUDGEMENT RATHER THAN AN
// OVERSIGHT. It is not a credential — reaching the report behind it still needs a
// session with a live grant, and 004 decides that in the database — and a
// bookmarkable, shareable link to "the show we are running on Thursday" is most
// of what makes a console usable by two people at once. What it does leak is that
// an activation with that id exists, to anybody who reads a browser history, and
// that is a uuid rather than a client's name.

import type { ReportName } from "../api/endpoints.js";

export type Route =
  /** The login screen. Also where an ended session lands. */
  | { readonly kind: "sign_in" }
  /**
   * Signed in with nothing chosen yet: the list of activations this session may
   * read.
   *
   * It used to be a box you pasted an identifier into, because there was no
   * endpoint answering "which activations may I read?" and a browser guessing at
   * one is a browser that will guess in the direction of showing something.
   * `GET /v1/activations` arrived on 2026-08-28 and this route now asks it.
   */
  | { readonly kind: "choose" }
  /**
   * One report about one activation.
   *
   * ONE ROUTE FOR FOUR REPORTS RATHER THAN FOUR ROUTES, because they differ in
   * exactly one value and REPORT_ENDPOINTS is already keyed by it. A route per
   * report would be four places to add the fifth one, and the compiler would
   * name none of them; this way `report` is a `ReportName` and a report added to
   * the service's constant fails to compile until it has a screen.
   */
  | { readonly kind: "report"; readonly report: ReportName; readonly activationId: string }
  /**
   * The screen in the hall: assembling and publishing one activation's kiosk
   * schedule.
   *
   * NOT A FIFTH REPORT, WHICH IS WHY IT IS NOT A `report` ROUTE. The four
   * reports are reads of the service that each record their own disclosure in
   * `report_access`; this one reads the activation directory and then does its
   * work in the browser, files no disclosure, and writes nothing anywhere yet.
   * Folding it into `ReportName` would put it in REPORT_ENDPOINTS, which is the
   * service's own constant naming urls that exist, and it would inherit the
   * tab strip's argument about which reads are worth an audit row.
   *
   * It carries the activation id for the same reason a report does, and with
   * the same judgement behind it — see the note above about what is in a url.
   *
   * `itemId` is one content item, a segment under Content (Gabe, 12 Oct,
   * decision C9): `/kiosk/content/<itemId>`, so a reload keeps the item open
   * and it can be linked. It is a crew shortcut rather than a uuid, and it is
   * only ever present on the content panel; absent is the list. Optional
   * rather than null on every kiosk route, so a route somebody writes for
   * another panel cannot carry one.
   */
  | {
      readonly kind: "kiosk";
      readonly activationId: string;
      readonly panel: KioskPanel;
      readonly itemId?: string;
    }
  /**
   * The capture site for this activation, split into panels the way the kiosk is.
   *
   * NOT A REPORT. It reads nothing that writes `report_access`. Folding it into
   * ReportName would put a page that does not exist as an endpoint into
   * REPORT_ENDPOINTS.
   */
  | { readonly kind: "registration"; readonly activationId: string; readonly panel: RegistrationPanel }
  /**
   * The activation's own record: name, hall, clock, run, and hall hours.
   *
   * NOT A REPORT AND NOT THE KIOSK. The four reports file `report_access`. The
   * kiosk page reads these facts and must not write them — Kiosk.tsx's header
   * is the argument. This is the write: `POST /v1/admin/activation-amendments`.
   * The segment is `details` so it does not collide with operator, kiosk,
   * registration, tablets, or any report name.
   */
  | { readonly kind: "details"; readonly activationId: string }
  /**
   * Who has played on this activation, one person's history, and the tape.
   *
   * NOT A REPORT. Scores live in the game's own store — FortNine Firestore for
   * Ride, the first game on this desk — and this screen reads them through the
   * XP API. A later activation whose game is not Ride gets a sibling route, not
   * a flag on this one. Folding it into ReportName would put it in
   * REPORT_ENDPOINTS and inherit `report_access`, which this read does not file.
   *
 * `panel` is the roster, the board, or the tape. A rider id is a page under
 * the roster, not another panel, so a bookmark still opens the same person.
   */
  | {
      readonly kind: "ride";
      readonly activationId: string;
      readonly riderId: string | null;
      readonly panel: RidePanel;
    }
  /**
   * Who registered at this activation, and one of them in full.
   *
   * NOT A REPORT, THOUGH IT IS THE MOST REPORT-LIKE ROUTE HERE AND THAT IS WORTH
   * SPELLING OUT. Both endpoints behind it do file `report_access`, which is the
   * property the four `report` routes share and the kiosk and ride routes do
   * not. What keeps it out of `ReportName` is where that name comes from:
   * REPORT_ENDPOINTS is the service's own constant, and reports/endpoints.ts
   * keeps ATTENDEE_ENDPOINTS as a sibling of it precisely so that these two urls
   * do not make the console's exhaustive switch demand a sixth report screen.
   * Folding them in would also give them a tab in the strip's own argument about
   * which reads are the day's numbers, and this is not one of those: it is a
   * named person's record.
   *
   * `attendeeRef` is a segment rather than a panel, the same shape as a rider on
   * the Ride desk, so a bookmark still opens the same person. It carries the
   * same judgement as the activation id above and a little more of it: what a
   * url in a browser history leaks here is that a registration with that
   * reference exists at that activation, which is a uuid rather than a name or
   * an address. The record behind it still needs a nova_admin session with a
   * proved second factor, and the service decides that.
   */
  | { readonly kind: "people"; readonly activationId: string; readonly attendeeRef: string | null }
  /**
   * The addresses typed at this activation that were never proved, and the tally
   * of the domains they were at.
   *
   * NOT A REPORT, ON `people`'S REASONING EXACTLY. The endpoint behind it files
   * a `report_access` row, which is the property the four `report` routes share.
   * What keeps it out of `ReportName` is where that name comes from:
   * reports/endpoints.ts holds UNVERIFIED_ATTEMPTS_ENDPOINT as a sibling of
   * REPORT_ENDPOINTS and says in its own comment that it is a sibling so the
   * console's exhaustive switch is not made to demand a sixth report screen.
   * Two files now depend on that constant staying where it is, and this is the
   * second.
   *
   * A SIBLING OF `people` RATHER THAN A SEGMENT UNDER IT, WHICH WAS THE FIRST
   * INSTINCT AND IS WRONG TWICE OVER.
   *
   * The mechanical objection first, because it is the one that would have been
   * found in a day: `people` consumes its third segment as an attendee
   * reference, so `/activations/x/people/unverified-attempts` already parses,
   * as the person whose reference is the literal string "unverified-attempts".
   * It would be a url that works, reaches the record screen, asks the service
   * for that reference and draws a not-found. Fixing that needs either a fourth
   * segment, which this file's regular expression does not have and should not
   * grow for one screen, or a reserved word in the reference position, which is
   * a rule about what the service's identifiers may not be, enforced in a
   * browser, in a file that cannot see them.
   *
   * The objection that would still stand if the paths were free: nesting states
   * a containment, and this one is false. The service's own `subset_note` says
   * the row is deleted the moment a mailbox is proved, so nobody who registered
   * is in this list and nobody in this list registered. A path reading "the
   * unverified attempts of the people at this activation" would assert, in the
   * one part of the screen somebody pastes into a message, the exact reading
   * that note exists to deny.
   *
   * NOT A PANEL EITHER, for the reason the strip's panels are panels: a kiosk
   * or registration panel is one more view of one read. This is a second read,
   * with its own audit row, its own page size and its own access rule, and the
   * People screen would have to fetch it or not fetch it depending on which
   * panel was open. Two screens that link to each other are the honest shape of
   * two reads.
   *
   * `unverified-attempts` rather than `drop-off`, which breaks the rule
   * TABLETS_SEGMENT sets about naming what a screen is for rather than what it
   * calls, and breaks it deliberately. Drop-off is an interpretation: it says
   * these are people who started and gave up, and the operator module's header
   * spends a paragraph on why some of them are strangers who mistyped and were
   * never here at all. The literal name is the one that does not decide that in
   * the address bar. It coinciding with the endpoint's last segment is not the
   * reason for it.
   *
   * It carries no per-address segment, and that absence is load-bearing rather
   * than a screen not built yet. There is no record page behind an address and
   * there must not be one: the note above about what a url leaks tolerates a
   * uuid, and an email address in a browser history, a referrer or a pasted
   * link is the personal datum itself rather than a handle for it. The only
   * identifier the service sends for a row is `attempt_id`, which opens nothing.
   */
  | { readonly kind: "unverified"; readonly activationId: string }
  /**
   * What the service recorded against this activation, newest first.
   *
   * NOT A REPORT, ON `people`'S AND `unverified`'S REASONING, AND THIS IS THE
   * THIRD FILE TO DEPEND ON THAT ARRANGEMENT. The endpoint files a
   * `report_access` row, which is the property the five `report` routes share.
   * reports/endpoints.ts holds ACTIVITY_ENDPOINT as a sibling of REPORT_ENDPOINTS
   * and says in its own comment that it is a sibling so the console's exhaustive
   * switch is not made to demand a sixth report screen. It also says, unlike the
   * other two siblings, that this one is provisional: it is a screen in its own
   * right and belongs in that object the day the console has one. When somebody
   * makes that move this route goes with it, and the compiler will say so.
   *
   * `activity` RATHER THAN `log`, `history` OR `audit`, AND THE RAIL CALLS IT
   * SOMETHING ELSE, WHICH IS A DELIBERATE MISMATCH RATHER THAN AN OVERSIGHT.
   *
   * The address bar gets the service's word. `activity` is the endpoint's last
   * segment, the name in the operator module and the audit's own vocabulary, and
   * the thing somebody does with this url is paste it into a support question
   * beside a `report_access` row they are asking about. Renaming it here would
   * mean the person answering had to know two words for one read. `audit` was
   * the tempting alternative and is an interpretation of the same kind
   * UNVERIFIED_SEGMENT rejects `drop-off` for: this feed is three sources joined,
   * and calling the whole of it the audit decides in the address bar that the
   * capture events are evidence rather than a record of a show.
   *
   * The rail says "Activity log", because RIDE_ACTIVITY_SEGMENT already gives
   * the Ride desk a panel labelled Activity, and the two are visible together
   * whenever somebody is on that desk. That is the collision the `/second-factor`
   * note above is about: two rail items reading the same word are two screens
   * people send each other to by mistake. The paths do not collide, because
   * Ride's is a third segment under `ride`, so the fix belongs on the label
   * rather than on the url.
   *
   * `rest === undefined` FOR THE REASON `unverified` REQUIRES IT: there is
   * nothing under this screen and there must not be. The last entry in the
   * service's `cannot_answer` refuses a page behind a row, because most of what
   * would be on one is what a person typed at a stand, so a route that answered
   * to `/activity/spine:000...` would be a url promising the one thing this feed
   * is built not to hand over.
   */
  | { readonly kind: "activity"; readonly activationId: string }
  /**
   * Winner selection from this activation's completed registrations.
   *
   * NOT A REPORT AND NOT A RIDE PANEL. The pool is People — anyone who finished
   * registration — and the write lives on the XP admin surface, not on a game
   * score store. A sixth Ride panel would hide a company-wide sweepstakes tool
   * behind one activation's arcade. Folding it into People would make a
   * ceremony look like a lookup.
   *
   * `rest === undefined` for the reason `unverified` and `activity` require it:
   * there is nothing under this screen. A third segment is a url this console
   * does not have.
   */
  | { readonly kind: "draw"; readonly activationId: string }
  /**
   * The prize wheel: Setup (join kind and slice labels), Prizes (weights and
   * stock on the slices, 156) and Results (every spin, collected, export).
   *
   * NOT A REGISTRATION PANEL. A show prints either ordinary registration or
   * the wheel. Hanging this under Registration would make a sibling surface
   * look like a toggle. Setup is `/wheel` itself, so a bookmark from before
   * the sub-pages opens what it always did; `/wheel/prizes` and
   * `/wheel/results` are the other two, and any other segment is a url this
   * console does not have.
   */
  | { readonly kind: "wheel"; readonly activationId: string; readonly panel: WheelPanel }
  /**
   * The leaderboard the game feeds (157, decision C11): Setup, the show's one
   * board record that the kiosk's leaderboard piece and the standalone board
   * also read, and Theme, what the board wears. Setup is `/board` itself and
   * Theme is `/board/theme`; any other segment is a url this console does not
   * have.
   */
  | { readonly kind: "board"; readonly activationId: string; readonly panel: BoardPanel }
  /**
   * The theme library as this show sees it, and one theme in it.
   *
   * UNDER THE ACTIVATION AND NOT AT THE ROOT, although a theme belongs to a
   * client rather than to a show. Everything the library page does beside
   * listing is about this show: which theme it wears, which app wears what,
   * and on a theme's own page which of this show's apps are wearing it. A
   * `/themes` at the root would be a list with none of those questions in it,
   * and the client's own page (`client_theme` below) is where the question
   * that is not about a show is asked.
   *
   * `themeId` is a segment rather than a panel, on `people`'s reasoning: a
   * bookmark to one theme opens the same theme. It is a uuid, or `th-nova` for
   * Nova house, which has no row and is drawn read-only.
   */
  | { readonly kind: "themes"; readonly activationId: string; readonly themeId: string | null }
  /**
   * Getting a tablet onto this activation: issuing an enrolment code.
   *
   * NOT A REPORT AND NOT THE KIOSK, WHICH IS WHY IT IS A THIRD KIND. It is a
   * write like the kiosk screen's, so it does not belong with the four reads for
   * the reason above. And it is not part of the kiosk screen, although both are
   * "setting a stand up", because a tablet and a television have nothing to do
   * with each other: they are different hardware, different credentials and
   * different people, and folding them into one screen would make the longest
   * page in this console out of two jobs that are never done together.
   *
   * It carries the activation id because a code enrols a tablet into exactly one
   * activation, and with the same judgement behind it as the routes above.
   */
  | { readonly kind: "tablets"; readonly activationId: string }
  /**
   * Adding an authenticator to the signed-in account.
   *
   * A route of its own rather than a panel on the landing screen, for two
   * reasons. It has to be linkable: the thing that sends somebody here is a 403
   * on a report, and "go to the console and look for the button" is the
   * instruction that gets misfollowed. And it has to survive a reload without
   * losing its place, because enrolment is the one flow here that involves
   * putting the laptop down and picking up a phone.
   *
   * It carries nothing in the path. The enrolment is identified by the session
   * that asked for it, the secret is never in a url, and there is no per-person
   * segment to leak — see the note above about what is deliberately not in a url.
   */
  | { readonly kind: "enrol" }
  /**
   * Who this sign-in is, and what it is allowed to do.
   *
   * The first of the three settings pages and the one the rail points at.
   * Nothing on it belongs to a show, which is why it is estate-level rather
   * than nested under an activation.
   *
   * A ROUTE OF ITS OWN RATHER THAN A PANEL ON `enrol`, AND THE REASON IS THAT
   * `enrol` IS ALREADY LINKED TO FROM A REFUSAL. Nine places navigate to it and
   * one of them is the button under a 403 on a report; turning it into a route
   * that carries a panel would mean every one of those had to name a panel, and
   * the one that got it wrong would send somebody chasing an authenticator to a
   * page about email. The kiosk and registration panels are panels because they
   * hang off one activation id and cannot be reached without it. These three
   * hang off nothing, so they are siblings in the same way `choose` and
   * `clients` are.
   */
  | { readonly kind: "account" }
  /**
   * Which emails this sign-in gets.
   *
   * Nothing behind it yet, and the page says so rather than drawing switches.
   * See Notices.tsx: the service sends one kind of mail and it is the login
   * code, so a page of toggles here would be a page of controls that save
   * nowhere.
   */
  | { readonly kind: "notices" }
  /**
   * Who can sign in to the console, how much of it they reach, and which
   * clients (Console Team, 149 and 150). Estate-level, beside Settings in the
   * rail's Account group, and advertised to an Admin only; the service refuses
   * the list to anybody else, which is what a deep link meets.
   */
  | { readonly kind: "team" }
  /**
   * The companies a Nova admin can attach a show to.
   *
   * Estate-level, like the activation directory, which is why it is not nested
   * under an activation and why it is not a report: listing clients files no
   * `report_access` row. A client viewer is not advertised this route — they
   * have one client, and a list of other companies would be 010's leak — but
   * the route itself is not gated by role. App.tsx makes that argument for
   * enrolment; the service refuses the list.
   */
  | { readonly kind: "clients" }
  /**
   * One company, by the identifier 010 put on the row.
   *
   * The path carries the id for the same reason an activation does, and with
   * the same judgement: it is not a credential, and a bookmark to "the client
   * we opened last week" is most of what makes two people able to talk about
   * the same row. What it leaks is that a client with that id exists.
   */
  | { readonly kind: "client"; readonly clientId: string }
  /**
   * One company's default theme, and a theme opened from there.
   *
   * A KIND OF ITS OWN RATHER THAN A PAGE FIELD ON `client`. The record's route
   * is a bookmark people already hold and its shape is asserted exactly, so a
   * second page beside it is a sibling with its own segment (the rail's
   * Details and Theme under Clients) rather than a field every existing
   * `client` route would now have to carry.
   *
   * `/clients/:id/theme/:themeId` opens a theme without a show around it,
   * which is the one place the theme page has no "which of this show's apps
   * wear it" to answer, and it says so rather than drawing switches.
   */
  | { readonly kind: "client_theme"; readonly clientId: string; readonly themeId: string | null }
  /**
   * A path this console does not have.
   *
   * Named rather than redirected to the home page. A redirect makes a typo look
   * like a working link and hides the one case that matters — a bookmark to a
   * route that was renamed — behind a page that looks fine.
   */
  | { readonly kind: "not_found"; readonly path: string };

/**
 * The path segment each report answers on, and the empty string for the one
 * that answers on the activation itself.
 *
 * `operator` has no segment because it is what opening an activation means: a
 * client following a link from the list wants the day's numbers, and
 * `/activations/:id/operator` would make the ordinary case the one with the
 * extra word in it. The other three are named, and the names are the report's
 * rather than the endpoint's — `opt-in` rather than `optIn` — because this
 * string is in the address bar of somebody who did not write it.
 *
 * Keyed by ReportName, so a fifth report in the service's constant is a
 * compile error here rather than a screen nothing links to.
 */
const REPORT_SEGMENT: Readonly<Record<ReportName, string>> = {
  operator: "",
  reconciliation: "reconciliation",
  optIn: "opt-in",
  export: "export",
  survey: "survey",
};

/**
 * The two segments that are not reports.
 *
 * Kept out of REPORT_SEGMENT rather than added to it, because that table is
 * keyed by ReportName and is what makes "every report has a screen" a compile
 * error. A non-report in it would be a value that has to be excluded again
 * everywhere the table is iterated.
 *
 * `tablets` rather than `enrolment-codes`, because this string is in the address
 * bar of somebody who did not write it — the same reason `opt-in` is not
 * `optIn` — and because it names what the screen is for rather than the endpoint
 * it calls. It is also deliberately not `enrolment`: `/second-factor` is where
 * a person enrols an authenticator, and two screens whose urls both said
 * "enrolment" would be two screens people send each other links to by mistake.
 */
const KIOSK_SEGMENT = "kiosk";
const TABLETS_SEGMENT = "tablets";
const REGISTRATION_SEGMENT = "registration";
const DETAILS_SEGMENT = "details";
const RIDE_SEGMENT = "ride";
const RIDE_ACTIVITY_SEGMENT = "activity";
const RIDE_LEADERBOARD_SEGMENT = "leaderboard";
const RIDE_SPONSOR_SEGMENT = "sponsor";
const RIDE_MAIL_SEGMENT = "mail";
/**
 * `people` rather than `attendees`, on the same rule as `tablets` above: this
 * string is in the address bar of somebody who did not write it, and it names
 * what the screen is for rather than the endpoint it calls. The service's own
 * urls say attendees, which is the right word for a route about rows and the
 * wrong one for a link somebody pastes into a message.
 */
const PEOPLE_SEGMENT = "people";
/**
 * Kept beside `people` because the two screens link to each other and a reader
 * comparing the two segments should find them together. The argument for the
 * word itself is on the route, where the case against `drop-off` is made.
 */
const UNVERIFIED_SEGMENT = "unverified-attempts";
/**
 * The same word RIDE_ACTIVITY_SEGMENT uses, one level shallower, and the route
 * above argues for the repetition and for the rail's different label.
 *
 * Declared here rather than reusing that constant. They are equal today and
 * have nothing to do with each other: that one is a panel of a game's own tape,
 * this one is the service's record of a show, and a shared constant would make
 * renaming either of them rename both.
 */
const ACTIVITY_SEGMENT = "activity";
/**
 * The reader's word for picking winners, not the endpoint's
 * `activation-draws`. Same rule as `people` versus `attendees`.
 */
const DRAW_SEGMENT = "draw";
/**
 * The reader's word for the prize wheel, not the endpoint's
 * `activation-wheels`. Same rule as `draw` versus `activation-draws`.
 */
const WHEEL_SEGMENT = "wheel";
/**
 * Plural for the library, which is what the page is: this show's choice among
 * many. The client's page is singular, `theme`, because it sets one default.
 */
const THEMES_SEGMENT = "themes";
const CLIENT_THEME_SEGMENT = "theme";

/** Roster, tape, board, sponsor branding, or mail switches. A rider is a segment, not a panel. */
export type RidePanel = "roster" | "activity" | "leaderboard" | "sponsor" | "mail";

/** The wheel's pages, in the address bar: Setup is the bare `/wheel`. */
export type WheelPanel = "setup" | "prizes" | "results";

const WHEEL_PANEL_SEGMENT: Readonly<Record<Exclude<WheelPanel, "setup">, string>> = {
  prizes: "prizes",
  results: "results",
};

/** The board's pages, in the address bar: Setup is the bare `/board`. */
export type BoardPanel = "setup" | "theme";

/** The reader's word for the leaderboard, as the design's rail has it. */
const BOARD_SEGMENT = "board";
const BOARD_THEME_SEGMENT = "theme";

/** The kiosk page's own strip, in the address bar rather than only in React state. */
export type KioskPanel = "schedule" | "content" | "screens" | "devices" | "theme";

const KIOSK_PANEL_SEGMENT: Readonly<Record<KioskPanel, string>> = {
  schedule: "",
  content: "content",
  screens: "screens",
  devices: "devices",
  // What the hall screens wear around the content: the theme library's page
  // for the kiosk, drawn by SurfaceTheme rather than by Kiosk.tsx.
  theme: "theme",
};

/**
 * The registration page's own strip, same shape as the kiosk's: the default
 * panel is the empty segment so `/registration` is Open rather than a word.
 */
export type RegistrationPanel = "open" | "capture" | "setup" | "legal" | "emails" | "theme" | "publish";

const REGISTRATION_PANEL_SEGMENT: Readonly<Record<RegistrationPanel, string>> = {
  open: "",
  capture: "capture",
  setup: "setup",
  legal: "legal",
  // The show's own emails (160, decision C7): the passcode's words, the
  // registration confirmation and the post-show follow-ups.
  emails: "emails",
  theme: "theme",
  publish: "publish",
};

const ACTIVATION = /^\/activations\/([^/]+)(?:\/([^/]+)(?:\/([^/]+)(?:\/([^/]+))?)?)?$/;
const CLIENT = /^\/clients\/([^/]+)(?:\/([^/]+)(?:\/([^/]+))?)?$/;

/**
 * The route for a path.
 *
 * Pure and total: every string in, exactly one route out, and no throw. It is
 * called on every navigation including the first, where the string comes from
 * whatever a person's bookmark says, so a path this function could throw on
 * would be a blank page reached by a url.
 */
export function routeFor(pathname: string): Route {
  const path = normalise(pathname);

  if (path === "/" || path === "") return { kind: "choose" };
  if (path === "/sign-in") return { kind: "sign_in" };
  if (path === "/settings") return { kind: "account" };
  if (path === "/settings/sign-in") return { kind: "enrol" };
  if (path === "/settings/notices") return { kind: "notices" };
  if (path === "/team") return { kind: "team" };
  // THE OLD ADDRESS, KEPT, AND NOT MERELY OUT OF POLITENESS TO BOOKMARKS. The
  // thing that sends somebody to that page is a 403 on a report, and the
  // instruction that goes with it — spoken, in a message, in whatever Nova told
  // a client last month — is a url. `pathFor` answers with the settings
  // spelling, so nothing new is written in this form and the round trip a route
  // makes through its own path is unaffected; this only means the sentence
  // somebody wrote down a year ago still lands where it used to.
  if (path === "/second-factor") return { kind: "enrol" };
  if (path === "/clients") return { kind: "clients" };

  const client = CLIENT.exec(path);
  const clientId = client?.[1];
  if (clientId !== undefined && clientId !== "") {
    const page = client?.[2];
    const rest = client?.[3];
    if (page === undefined) return { kind: "client", clientId: safeDecode(clientId) };
    // The only page under a company. Anything else under one stays not-found,
    // which is what `/clients/x/docs` has always been.
    if (page === CLIENT_THEME_SEGMENT) {
      return {
        kind: "client_theme",
        clientId: safeDecode(clientId),
        themeId: rest === undefined || rest === "" ? null : safeDecode(rest),
      };
    }
    return { kind: "not_found", path };
  }

  const activation = ACTIVATION.exec(path);
  const id = activation?.[1];
  if (id !== undefined && id !== "") {
    const segment = activation?.[2];
    const rest = activation?.[3];
    const deeper = activation?.[4];
    // A fourth segment is one kiosk content item (C9) and nothing else: every
    // other page under an activation is two segments deep at most, so a
    // fourth there is a url this console does not have.
    if (deeper !== undefined && (segment !== KIOSK_SEGMENT || rest !== KIOSK_PANEL_SEGMENT.content || deeper === "")) {
      return { kind: "not_found", path };
    }
    if (segment === KIOSK_SEGMENT) {
      const panel = panelFor(rest);
      if (panel === null) return { kind: "not_found", path };
      if (deeper !== undefined) return { kind: "kiosk", activationId: safeDecode(id), panel, itemId: safeDecode(deeper) };
      return { kind: "kiosk", activationId: safeDecode(id), panel };
    }
    if (segment === TABLETS_SEGMENT && rest === undefined) {
      return { kind: "tablets", activationId: safeDecode(id) };
    }
    if (segment === REGISTRATION_SEGMENT) {
      const panel = registrationPanelFor(rest);
      if (panel === null) return { kind: "not_found", path };
      return { kind: "registration", activationId: safeDecode(id), panel };
    }
    if (segment === DETAILS_SEGMENT && rest === undefined) {
      return { kind: "details", activationId: safeDecode(id) };
    }
    if (segment === RIDE_SEGMENT) {
      if (rest === RIDE_ACTIVITY_SEGMENT) {
        return { kind: "ride", activationId: safeDecode(id), riderId: null, panel: "activity" };
      }
      if (rest === RIDE_LEADERBOARD_SEGMENT) {
        return { kind: "ride", activationId: safeDecode(id), riderId: null, panel: "leaderboard" };
      }
      if (rest === RIDE_SPONSOR_SEGMENT) {
        return { kind: "ride", activationId: safeDecode(id), riderId: null, panel: "sponsor" };
      }
      if (rest === RIDE_MAIL_SEGMENT) {
        return { kind: "ride", activationId: safeDecode(id), riderId: null, panel: "mail" };
      }
      return {
        kind: "ride",
        activationId: safeDecode(id),
        riderId: rest === undefined || rest === "" ? null : safeDecode(rest),
        panel: "roster",
      };
    }
    if (segment === PEOPLE_SEGMENT) {
      return {
        kind: "people",
        activationId: safeDecode(id),
        attendeeRef: rest === undefined || rest === "" ? null : safeDecode(rest),
      };
    }
    // `rest === undefined` for the reason `tablets` and `details` require it:
    // there is nothing under this screen, so a third segment is a url this
    // console does not have rather than one it should quietly ignore. A page
    // that answered to `/unverified-attempts/anything` would make a mistyped
    // deep link look like it worked.
    if (segment === UNVERIFIED_SEGMENT && rest === undefined) {
      return { kind: "unverified", activationId: safeDecode(id) };
    }
    // `rest === undefined` again, and here it is about the one thing the feed
    // refuses rather than about a mistyped link: see the route.
    if (segment === ACTIVITY_SEGMENT && rest === undefined) {
      return { kind: "activity", activationId: safeDecode(id) };
    }
    if (segment === DRAW_SEGMENT && rest === undefined) {
      return { kind: "draw", activationId: safeDecode(id) };
    }
    if (segment === WHEEL_SEGMENT && rest === undefined) {
      return { kind: "wheel", activationId: safeDecode(id), panel: "setup" };
    }
    if (segment === WHEEL_SEGMENT && rest === WHEEL_PANEL_SEGMENT.prizes) {
      return { kind: "wheel", activationId: safeDecode(id), panel: "prizes" };
    }
    if (segment === WHEEL_SEGMENT && rest === WHEEL_PANEL_SEGMENT.results) {
      return { kind: "wheel", activationId: safeDecode(id), panel: "results" };
    }
    if (segment === BOARD_SEGMENT && rest === undefined) {
      return { kind: "board", activationId: safeDecode(id), panel: "setup" };
    }
    if (segment === BOARD_SEGMENT && rest === BOARD_THEME_SEGMENT) {
      return { kind: "board", activationId: safeDecode(id), panel: "theme" };
    }
    if (segment === THEMES_SEGMENT) {
      return {
        kind: "themes",
        activationId: safeDecode(id),
        themeId: rest === undefined || rest === "" ? null : safeDecode(rest),
      };
    }

    const report = reportFor(segment);
    // A segment this console has no report for is not-found rather than the
    // activation's own screen. Falling back would make /activations/x/exprot a
    // page that looks like it worked, which is the argument the not_found route
    // is already written on.
    if (report !== null) {
      // Decoded here rather than at the point of use, so that the rest of the
      // application handles an activation id and never a percent-encoded one.
      // api/endpoints.ts re-encodes it on the way into a url, which is the
      // pairing that keeps an id with an awkward character in it round-tripping.
      return { kind: "report", report, activationId: safeDecode(id) };
    }
  }

  return { kind: "not_found", path };
}

/**
 * The path for a route.
 *
 * The inverse of `routeFor` for every route except `not_found`, and check.mjs
 * asserts the round trip rather than trusting it. Two functions that disagree is
 * a link that navigates somewhere other than where it points, which is the class
 * of bug a router would have removed and which is cheap to assert instead.
 */
export function pathFor(route: Route): string {
  switch (route.kind) {
    case "sign_in":
      return "/sign-in";
    case "choose":
      return "/";
    case "account":
      return "/settings";
    case "enrol":
      return "/settings/sign-in";
    case "notices":
      return "/settings/notices";
    case "team":
      return "/team";
    case "clients":
      return "/clients";
    case "client":
      return `/clients/${encodeURIComponent(route.clientId)}`;
    case "client_theme": {
      const base = `/clients/${encodeURIComponent(route.clientId)}/${CLIENT_THEME_SEGMENT}`;
      return route.themeId === null ? base : `${base}/${encodeURIComponent(route.themeId)}`;
    }
    case "report": {
      const segment = REPORT_SEGMENT[route.report];
      const base = `/activations/${encodeURIComponent(route.activationId)}`;
      return segment === "" ? base : `${base}/${segment}`;
    }
    case "kiosk": {
      const base = `/activations/${encodeURIComponent(route.activationId)}/${KIOSK_SEGMENT}`;
      const panel = KIOSK_PANEL_SEGMENT[route.panel];
      if (route.panel === "content" && route.itemId !== undefined && route.itemId !== "") {
        return `${base}/${panel}/${encodeURIComponent(route.itemId)}`;
      }
      return panel === "" ? base : `${base}/${panel}`;
    }
    case "registration": {
      const base = `/activations/${encodeURIComponent(route.activationId)}/${REGISTRATION_SEGMENT}`;
      const panel = REGISTRATION_PANEL_SEGMENT[route.panel];
      return panel === "" ? base : `${base}/${panel}`;
    }
    case "tablets":
      return `/activations/${encodeURIComponent(route.activationId)}/${TABLETS_SEGMENT}`;
    case "details":
      return `/activations/${encodeURIComponent(route.activationId)}/${DETAILS_SEGMENT}`;
    case "ride": {
      const base = `/activations/${encodeURIComponent(route.activationId)}/${RIDE_SEGMENT}`;
      if (route.panel === "activity") return `${base}/${RIDE_ACTIVITY_SEGMENT}`;
      if (route.panel === "leaderboard") return `${base}/${RIDE_LEADERBOARD_SEGMENT}`;
      if (route.panel === "sponsor") return `${base}/${RIDE_SPONSOR_SEGMENT}`;
      if (route.panel === "mail") return `${base}/${RIDE_MAIL_SEGMENT}`;
      return route.riderId === null ? base : `${base}/${encodeURIComponent(route.riderId)}`;
    }
    case "people": {
      const base = `/activations/${encodeURIComponent(route.activationId)}/${PEOPLE_SEGMENT}`;
      return route.attendeeRef === null ? base : `${base}/${encodeURIComponent(route.attendeeRef)}`;
    }
    case "unverified":
      return `/activations/${encodeURIComponent(route.activationId)}/${UNVERIFIED_SEGMENT}`;
    case "activity":
      return `/activations/${encodeURIComponent(route.activationId)}/${ACTIVITY_SEGMENT}`;
    case "draw":
      return `/activations/${encodeURIComponent(route.activationId)}/${DRAW_SEGMENT}`;
    case "wheel": {
      const base = `/activations/${encodeURIComponent(route.activationId)}/${WHEEL_SEGMENT}`;
      return route.panel === "setup" ? base : `${base}/${WHEEL_PANEL_SEGMENT[route.panel]}`;
    }
    case "board": {
      const base = `/activations/${encodeURIComponent(route.activationId)}/${BOARD_SEGMENT}`;
      return route.panel === "setup" ? base : `${base}/${BOARD_THEME_SEGMENT}`;
    }
    case "themes": {
      const base = `/activations/${encodeURIComponent(route.activationId)}/${THEMES_SEGMENT}`;
      return route.themeId === null ? base : `${base}/${encodeURIComponent(route.themeId)}`;
    }
    case "not_found":
      return route.path;
  }
}

/**
 * The report a path segment names, or null.
 *
 * Reads the table rather than switching on strings, so the two directions
 * cannot disagree: the same object decides what `pathFor` writes and what this
 * accepts, and check.mjs asserts the round trip for every report there is.
 */
function panelFor(segment: string | undefined): KioskPanel | null {
  const wanted = segment ?? "";
  for (const [panel, known] of Object.entries(KIOSK_PANEL_SEGMENT) as [KioskPanel, string][]) {
    if (known === wanted) return panel;
  }
  return null;
}

function registrationPanelFor(segment: string | undefined): RegistrationPanel | null {
  const wanted = segment ?? "";
  for (const [panel, known] of Object.entries(REGISTRATION_PANEL_SEGMENT) as [RegistrationPanel, string][]) {
    if (known === wanted) return panel;
  }
  return null;
}

function reportFor(segment: string | undefined): ReportName | null {
  const wanted = segment ?? "";
  for (const [report, known] of Object.entries(REPORT_SEGMENT) as [ReportName, string][]) {
    if (known === wanted) return report;
  }
  return null;
}

/**
 * A trailing slash is the same route, and a repeated slash is not a route.
 *
 * Collapsed rather than refused, because `//activations/x` is what string
 * concatenation produces and nobody types it deliberately. The alternative is a
 * not-found page for a link that is obviously meant.
 */
function normalise(pathname: string): string {
  const collapsed = pathname.replace(/\/{2,}/g, "/");
  return collapsed.length > 1 && collapsed.endsWith("/") ? collapsed.slice(0, -1) : collapsed;
}

/**
 * `decodeURIComponent` without the throw.
 *
 * It throws on a lone `%`, which is a character somebody can put in the address
 * bar. Falling back to the raw segment produces an activation id the server will
 * not find, which is a not-found report rather than a blank page.
 */
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
